'use client'
import { useAtom } from 'jotai'
import { compact, filter, map, toNumber, uniq } from 'lodash'

import { useProviderDepartmentAppointmentCountsQuery } from '@docpace/manager-react-apollo'
import { ctxPracticeIdAtom } from '@docpace/shared-react-atoms'

interface UseAppointmentCountsProps {
    filterBy?: { key: 'appointmentTypeId' | 'providerId' | 'departmentId', value: any }
    groupByKey: 'appointmentTypeId' | 'providerId' | 'departmentId' | 'practiceId'
}

export interface AppointmentCountNode {
    practiceId?: string | null
    departmentId?: string | null
    providerId?: string | null
    appointmentTypeId?: string | null
    status?: string | null
    managerAppointmentCount?: any | null
    appointmentCount?: any | null
    // departmentAdminAppointmentCount?: Map<string, number> 
}

export interface AppointmentCountNodeData {
    __typename?: "RecentAppointmentCountsGrouped";
    practiceId?: string | null;
    departmentId?: string | null;
    providerId?: string | null;
    appointmentTypeId?: string | null;
    status?: string | null;
    managerAppointmentCount?: any | null;
    appointmentCount?: any | null;
}[]

export type AppointmentCountItem = { 
    managerAppointmentCount: number, 
    appointmentCount: number, 
    nodes: AppointmentCountNode[], 
    departmentAppointmentCounts: Map<string, number>  
    departmentManagerAppointmentCounts: Map<string, number>  
    appointmentTypeAppointmentCounts: Map<string, number>  
    appointmentTypeManagerAppointmentCounts: Map<string, number>  
    providerAppointmentCounts: Map<string, number>  
    providerManagerAppointmentCounts: Map<string, number>  
}

export function useAppointmentCounts(
    props: UseAppointmentCountsProps
){
    const { filterBy, groupByKey } = props
    const [ practiceId ] = useAtom(ctxPracticeIdAtom)
    const { data, loading: countsLoading } =
        useProviderDepartmentAppointmentCountsQuery({ 
            variables: { practiceId }, 
            skip: !practiceId, 
            fetchPolicy: 'cache-first' 
        })

    const rawNodeData = compact(data?.recentAppointmentCountsGroupeds?.nodes)

    // The upstream query can return multiple rows for the same logical count split by status.
    // Collapse those variants into a single status-agnostic row before we build totals.
    const normalizedNodeData = new Map<string, AppointmentCountNode>()
    rawNodeData.forEach((node) => {
        const key = [
            node.practiceId ?? '',
            node.departmentId ?? '',
            node.providerId ?? '',
            node.appointmentTypeId ?? '',
        ].join('::')
        const existingNode = normalizedNodeData.get(key)
        if (!existingNode) {
            normalizedNodeData.set(key, {
                ...node,
                status: null,
            })
            return
        }

        normalizedNodeData.set(key, {
            ...existingNode,
            status: null,
            appointmentCount: Math.max(
                toNumber(existingNode.appointmentCount),
                toNumber(node.appointmentCount)
            ),
            managerAppointmentCount: Math.max(
                toNumber(existingNode.managerAppointmentCount),
                toNumber(node.managerAppointmentCount)
            ),
        })
    })

    const nodeData = Array.from(normalizedNodeData.values())
    const nodes = filterBy
        ? filter(nodeData, (node) => node[filterBy.key] === filterBy.value)
        : nodeData
    
    const distinctProviderIds = compact(uniq(map(nodes, 'providerId')))
    const distinctDepartmentIds = compact(uniq(map(nodes, 'departmentId')))
    const distinctAppointmentTypeIds = compact(uniq(map(nodes, 'appointmentTypeId')))

    const appointmentCounts = new Map<string, AppointmentCountItem>() 

    // first, group the items by the groupByKey, compute the two appt counts, and set up some empty Map()s for the group
    nodes?.forEach(countItem => {
        const id = countItem[groupByKey]
        if(id){
            const existingCount = appointmentCounts.get(id)
            appointmentCounts.set(id, {
                appointmentCount: toNumber(existingCount?.appointmentCount ?? 0) + toNumber(countItem.appointmentCount),
                managerAppointmentCount: toNumber(existingCount?.managerAppointmentCount ?? 0) + toNumber(countItem.managerAppointmentCount),
                nodes: [...(existingCount?.nodes ?? []), countItem],
                departmentAppointmentCounts: new Map(),
                departmentManagerAppointmentCounts: new Map(),
                appointmentTypeAppointmentCounts: new Map(),
                appointmentTypeManagerAppointmentCounts: new Map(),
                providerAppointmentCounts: new Map(),
                providerManagerAppointmentCounts: new Map(),
            })
        }
    })

    appointmentCounts.forEach((countItem, key)=>{
        const departmentAppointmentCounts = new Map<string, number>()
        const departmentManagerAppointmentCounts = new Map<string, number>()
        const appointmentTypeAppointmentCounts = new Map<string, number>()
        const appointmentTypeManagerAppointmentCounts = new Map<string, number>()
        const providerAppointmentCounts = new Map<string, number>()
        const providerManagerAppointmentCounts = new Map<string, number>()
        countItem.nodes?.forEach((innerCountItem) => {
            const departmentId = innerCountItem['departmentId']
            if(departmentId){
                const departmentExistingCount = toNumber(departmentAppointmentCounts.get(departmentId) ?? 0)
                departmentAppointmentCounts.set(departmentId, departmentExistingCount + toNumber(innerCountItem.appointmentCount))
                const departmentExistingManagerCount = toNumber(departmentManagerAppointmentCounts.get(departmentId) ?? 0)
                departmentManagerAppointmentCounts.set(departmentId, departmentExistingManagerCount + toNumber(innerCountItem.managerAppointmentCount))
            }
            const appointmentTypeId = innerCountItem['appointmentTypeId']
            if(appointmentTypeId){
                const appointmentTypeExistingCount = toNumber(appointmentTypeAppointmentCounts.get(appointmentTypeId) ?? 0)
                appointmentTypeAppointmentCounts.set(appointmentTypeId, appointmentTypeExistingCount + toNumber(innerCountItem.appointmentCount))
                const appointmentTypeExistingManagerCount = toNumber(appointmentTypeManagerAppointmentCounts.get(appointmentTypeId) ?? 0)
                appointmentTypeManagerAppointmentCounts.set(appointmentTypeId, appointmentTypeExistingManagerCount + toNumber(innerCountItem.managerAppointmentCount))
            }
            const providerId = innerCountItem['providerId']
            if(providerId){
                const providerExistingCount = toNumber(providerAppointmentCounts.get(providerId) ?? 0)
                providerAppointmentCounts.set(providerId, providerExistingCount + toNumber(innerCountItem.appointmentCount))
                const providerExistingManagerCount = toNumber(providerManagerAppointmentCounts.get(providerId) ?? 0)
                providerManagerAppointmentCounts.set(providerId, providerExistingManagerCount + toNumber(innerCountItem.managerAppointmentCount))
            }
        })
        appointmentCounts.set(key, {
            ...countItem,
            departmentAppointmentCounts,
            departmentManagerAppointmentCounts,
            appointmentTypeAppointmentCounts,
            appointmentTypeManagerAppointmentCounts,
            providerAppointmentCounts,
            providerManagerAppointmentCounts,
        })
    })

    return {
        isLoading: countsLoading,
        distinctAppointmentTypeIds,
        distinctDepartmentIds,
        distinctProviderIds,
        appointmentCounts,
    }
}
